import React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

// ========================================
// CARD COMPONENT - Professional Design
// ========================================

export const Card = ({ children, className, padding = 'p-3 lg:p-4', ...props }) => (
  <div
    className={clsx(
      'bg-white rounded-xl border border-secondary-100/60',
      'shadow-md hover:shadow-lg transition-shadow duration-200',
      padding,
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// ========================================
// BUTTON COMPONENT - Professional Design
// ========================================

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className,
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  ...props
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:from-primary-700 hover:to-primary-600 shadow-md hover:shadow-lg',
    secondary: 'bg-secondary-700 text-white hover:bg-secondary-800 shadow-md hover:shadow-lg',
    outline: 'bg-white border-2 border-secondary-200 text-secondary-700 hover:border-primary-300 hover:bg-primary-50/30',
    ghost: 'bg-transparent text-secondary-600 hover:bg-secondary-100 hover:text-secondary-800',
    danger: 'bg-gradient-to-r from-danger-600 to-danger-500 text-white hover:from-danger-700 hover:to-danger-600 shadow-md hover:shadow-lg',
    success: 'bg-gradient-to-r from-success-600 to-success-500 text-white hover:from-success-700 hover:to-success-600 shadow-md hover:shadow-lg',
    warning: 'bg-gradient-to-r from-warning-600 to-warning-500 text-white hover:from-warning-700 hover:to-warning-600 shadow-md hover:shadow-lg',
  };

  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs rounded-lg min-h-[30px] lg:min-h-[32px]',
    md: 'px-3.5 py-2 text-sm rounded-lg min-h-[36px] lg:min-h-[38px]',
    lg: 'px-5 py-2.5 text-sm rounded-xl min-h-[42px]',
  };

  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center gap-1.5',
        'font-semibold transition-all duration-200',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-primary-500/40',
        'active:scale-[0.98]',
        'whitespace-nowrap',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {!loading && icon && iconPosition === 'left' && <span className="w-4 h-4">{icon}</span>}
      <span>{children}</span>
      {!loading && icon && iconPosition === 'right' && <span className="w-4 h-4">{icon}</span>}
    </button>
  );
};

// ========================================
// INPUT COMPONENT - Professional Design
// ========================================

export const Input = ({ label, error, icon, rightIcon, required, className, inputClassName = '', type = 'text', ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}{required && <span className="text-red-500">*</span>}</label>}
    <div className="relative">
      {icon && (
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-400">{icon}</div>
      )}
      <input
        type={type}
        className={clsx(
          'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm',
          inputClassName,
          'bg-white text-secondary-900 placeholder:text-secondary-400',
          'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
          'hover:border-secondary-300',
          error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
          icon && 'pl-10',
          rightIcon && 'pr-10',
          'transition-all duration-200'
        )}
        {...props}
      />
      {rightIcon && (
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2">{rightIcon}</div>
      )}
    </div>
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// TEXTAREA COMPONENT - Professional Design
// ========================================

export const Textarea = ({ label, error, required, className, rows = 4, ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}{required && <span className="text-red-500">*</span>}</label>}
    <textarea
      rows={rows}
      className={clsx(
        'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm resize-y',
        'bg-white text-secondary-900 placeholder:text-secondary-400',
        'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
        'hover:border-secondary-300',
        error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
        'transition-all duration-200'
      )}
      {...props}
    />
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// SELECT COMPONENT - Professional Design
// ========================================

export const Select = ({ label, error, required, options = [], placeholder, className, inputClassName = '', ...props }) => (
  <div className={clsx('space-y-1.5', className)}>
    {label && <label className="block text-sm font-semibold text-secondary-700">{label}{required && <span className="text-red-500">*</span>}</label>}
    <div className="relative">
      <select
        className={clsx(
          'w-full px-3.5 py-2.5 rounded-xl border-2 text-sm appearance-none bg-white',
          inputClassName,
          'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
          'hover:border-secondary-300',
          error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
          'transition-all duration-200 cursor-pointer'
        )}
        {...props}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {(Array.isArray(options) ? options : []).map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
    {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}
  </div>
);

// ========================================
// SEARCHABLE SELECT - Select with a search box (long lists, e.g. countries)
// onChange receives { target: { value } } so it's a drop-in for <Select>.
// The list is portaled to <body> so modals with overflow don't clip it.
// ========================================

export const SearchableSelect = ({
  label,
  error,
  required,
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [rect, setRect] = React.useState(null);
  const buttonRef = React.useRef(null);
  const listRef = React.useRef(null);
  const searchRef = React.useRef(null);

  const selected = options.find((o) => o.value === value);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        String(o.value).toLowerCase() === q ||
        (o.keywords && o.keywords.includes(q))
    );
  }, [options, query]);

  const updateRect = React.useCallback(() => {
    if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
  }, []);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (option) => {
    onChange?.({ target: { value: option.value } });
    close();
    buttonRef.current?.focus();
  };

  React.useEffect(() => {
    if (!open) return undefined;
    updateRect();
    setActive(Math.max(0, filtered.findIndex((o) => o.value === value)));
    setTimeout(() => searchRef.current?.focus(), 0);
    const onDocDown = (e) => {
      if (!buttonRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) close();
    };
    window.addEventListener('mousedown', onDocDown);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[active]) pick(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
      buttonRef.current?.focus();
    }
  };

  // Open upward when there isn't room below the field
  const openUp = rect && window.innerHeight - rect.bottom < 300 && rect.top > 300;

  return (
    <div className={clsx('space-y-1.5', className)}>
      {label && <label className="block text-sm font-semibold text-secondary-700">{label}{required && <span className="text-red-500">*</span>}</label>}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          className={clsx(
            'w-full px-3.5 py-2.5 pr-9 rounded-xl border-2 text-sm text-left bg-white truncate',
            'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none',
            'hover:border-secondary-300',
            error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/10' : 'border-secondary-200',
            'transition-all duration-200 cursor-pointer',
            !selected && 'text-secondary-400'
          )}
        >
          {selected ? selected.label : placeholder}
        </button>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && <p className="text-xs text-danger-600 font-medium">{error}</p>}

      {open && rect && createPortal(
        <div
          ref={listRef}
          className="fixed z-[70] bg-white dark:bg-slate-800 border border-secondary-200 dark:border-slate-600 rounded-xl shadow-xl overflow-hidden"
          style={{
            left: rect.left,
            width: rect.width,
            ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
          }}
        >
          <div className="p-2 border-b border-secondary-100 dark:border-slate-700">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 rounded-lg border border-secondary-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-secondary-800 dark:text-slate-100 focus:outline-none focus:border-primary-500"
            />
          </div>
          <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3.5 py-2 text-sm text-secondary-400">No results</li>
            )}
            {filtered.map((option, i) => (
              <li
                key={option.value}
                data-index={i}
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(option);
                }}
                className={clsx(
                  'px-3.5 py-2 text-sm cursor-pointer',
                  i === active ? 'bg-primary-50 dark:bg-slate-700' : '',
                  option.value === value
                    ? 'font-semibold text-primary-700 dark:text-primary-300'
                    : 'text-secondary-700 dark:text-slate-200'
                )}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

// ========================================
// BADGE COMPONENT - Professional Design
// ========================================

export const Badge = ({ children, variant = 'default', size = 'sm', className }) => {
  const variants = {
    default: 'bg-secondary-100 text-secondary-700 border-secondary-200',
    primary: 'bg-primary-100 text-primary-700 border-primary-200',
    success: 'bg-success-100 text-success-700 border-success-200',
    warning: 'bg-warning-100 text-warning-700 border-warning-200',
    danger: 'bg-danger-100 text-danger-700 border-danger-200',
    info: 'bg-info-100 text-info-700 border-info-200',
    new: 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border-primary-200',
    follow_up: 'bg-gradient-to-r from-warning-50 to-warning-100 text-warning-700 border-warning-200',
    cold: 'bg-gradient-to-r from-info-50 to-info-100 text-info-700 border-info-200',
    warm: 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 border-orange-200',
    hot: 'bg-gradient-to-r from-danger-50 to-danger-100 text-danger-700 border-danger-200',
    converted: 'bg-gradient-to-r from-success-50 to-success-100 text-success-700 border-success-200',
    closed: 'bg-gradient-to-r from-secondary-50 to-secondary-100 text-secondary-600 border-secondary-200',
    not_connected: 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-200',
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs rounded-lg',
    md: 'px-2.5 py-1 text-xs rounded-lg',
    lg: 'px-3 py-1.5 text-sm rounded-xl',
  };

  return (
    <span className={clsx(
      'inline-flex items-center font-semibold border',
      variants[variant] || variants.default,
      sizes[size],
      className
    )}>
      {children}
    </span>
  );
};

// ========================================
// STAT CARD COMPONENT - Professional Design
// ========================================

export const StatCard = ({ title, value, icon, trend, trendValue, color = 'primary' }) => {
  const colors = {
    primary: { icon: 'bg-gradient-to-br from-primary-500 to-primary-600', ring: 'ring-primary-500/20' },
    secondary: { icon: 'bg-gradient-to-br from-secondary-500 to-secondary-600', ring: 'ring-secondary-500/20' },
    success: { icon: 'bg-gradient-to-br from-success-500 to-success-600', ring: 'ring-success-500/20' },
    warning: { icon: 'bg-gradient-to-br from-warning-500 to-warning-600', ring: 'ring-warning-500/20' },
    danger: { icon: 'bg-gradient-to-br from-danger-500 to-danger-600', ring: 'ring-danger-500/20' },
    info: { icon: 'bg-gradient-to-br from-info-500 to-info-600', ring: 'ring-info-500/20' },
  };

  const colorStyles = colors[color] || colors.primary;

  return (
    <Card className="py-3 lg:py-4 hover:shadow-lg border-black/20 transition-all duration-200 group">
      <div className="flex items-center gap-3">
        {/* <div className={clsx(
          'w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg',
          colorStyles.icon,
          'ring-4',
          colorStyles.ring
        )}>
          <span className="text-white text-base lg:text-lg">{icon}</span>
        </div> */}
        <div className="flex-1 min-w-0">
          <p className="text-xs lg:text-sm text-secondary-500 font-medium leading-snug line-clamp-2" title={typeof title === 'string' ? title : undefined}>{title}</p>
          <p className="text-xl lg:text-2xl font-bold text-secondary-900">{value}</p>
          {/* {trend && (
            <span className={clsx(
              'text-xs font-semibold inline-flex items-center gap-0.5',
              trend === 'up' ? 'text-success-600' : 'text-danger-600'
            )}>
              {trend === 'up' ? '↑' : '↓'} {trendValue}
            </span>
          )} */}
        </div>
      </div>
    </Card>
  );
};

// ========================================
// TABLE COMPONENT - Professional Design
// ========================================

// `bounded` keeps long tables inside a viewport-height scroll box so the horizontal
// scrollbar stays visible (instead of only at the very bottom of the page) and the
// sticky header stays on screen. Pass bounded={false} for short tables in cards/modals.
export const Table = ({ children, className, bounded = true, wrapperClassName }) => (
  <div className={clsx('overflow-auto', bounded && 'max-h-[calc(100vh-220px)]', wrapperClassName)}>
    <table className={clsx('w-full', className)}>{children}</table>
  </div>
);

export const TableHead = ({ children, className }) => (
  <thead className={clsx('sticky top-0 z-10 bg-secondary-50 bg-gradient-to-r from-secondary-50 to-secondary-100 border-b-2 border-secondary-200/50', className)}>{children}</thead>
);

// Class for a right-pinned "Actions" column (apply to both the header cell and body cells)
// hover/focus-within lift the cell above the next row's sticky cell, so a
// tooltip opening downward (first row) isn't painted underneath it.
export const stickyActionCellClass = 'sticky right-0 z-[1] hover:z-[3] focus-within:z-[3] bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]';
export const stickyActionHeaderClass = 'sticky right-0 z-[2] bg-secondary-100 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]';

export const TableBody = ({ children, className }) => (
  <tbody className={clsx('divide-y divide-secondary-100', className)}>{children}</tbody>
);

export const TableRow = ({ children, className, onClick, hoverable = true }) => (
  <tr
    className={clsx(
      'transition-all duration-150',
      hoverable && 'hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent',
      onClick && 'cursor-pointer',
      className
    )}
    onClick={onClick}
  >
    {children}
  </tr>
);

export const TableCell = ({ children, className, header = false, ...props }) => (
  <td
    className={clsx(
      'px-4 py-3 lg:px-5 lg:py-3.5',
      header ? 'text-left text-xs font-bold text-secondary-600 uppercase tracking-wider' : 'text-sm text-secondary-700',
      className
    )}
    {...props}
  >
    {children}
  </td>
);

// ========================================
// LOADING SPINNER
// ========================================

export const Spinner = ({ size = 'md', className }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
  return (
    <div className={clsx('animate-spin text-primary-600', sizes[size], className)}>
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  );
};

// ========================================
// EMPTY STATE - Professional Design
// ========================================

export const EmptyState = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6">
    {icon && (
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary-100 to-secondary-50 flex items-center justify-center mb-4 shadow-inner">
        {icon}
      </div>
    )}
    <h3 className="text-lg font-bold text-secondary-900 mb-2">{title}</h3>
    {description && <p className="text-sm text-secondary-500 text-center max-w-sm mb-4">{description}</p>}
    {action}
  </div>
);

// ========================================
// MODAL COMPONENT - Professional Design
// ========================================

const openModalStack = [];

export const Modal = ({ isOpen, onClose, title, children, size = 'md', headerClassName = '' }) => {
  const titleId = React.useId();
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Close on Escape while open (only the top-most open modal reacts)
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const token = {};
    openModalStack.push(token);
    const handleKeyDown = (e) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && openModalStack[openModalStack.length - 1] === token) {
        onCloseRef.current?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const idx = openModalStack.indexOf(token);
      if (idx !== -1) openModalStack.splice(idx, 1);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
    full: 'sm:max-w-full sm:mx-4',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-gradient-to-br from-secondary-900/60 to-secondary-800/40 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        className={clsx(
        'relative bg-white w-full sm:rounded-2xl shadow-2xl',
        'min-h-screen sm:min-h-0 sm:max-h-[90vh]',
        'overflow-y-auto animate-scale-in',
        sizes[size],
        'border border-secondary-200/50'
      )}>
        <div className={clsx(
          'sticky top-0 z-10 bg-gradient-to-r from-white to-secondary-50 border-b border-secondary-200/50 px-5 py-4 flex items-center justify-between',
          // Opaque dark surface for the base header (see modal-header-base in index.css).
          // Skipped when a modal supplies its own dark:bg-[...] header gradient.
          !headerClassName && 'modal-header-base',
          headerClassName
        )}>
          <h2 id={titleId} className="text-lg font-bold text-secondary-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" title="Close" className="p-2 rounded-xl text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// ========================================
// TABS COMPONENT - Professional Design
// ========================================

export const Tabs = ({ tabs, activeTab, onChange }) => {
  const scrollRef = React.useRef(null);

  const handleScrollTo = (tabValue) => {
    onChange(tabValue);
    // Scroll the active tab into view after a tick
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      const activeBtn = el.querySelector(`[data-tab="${tabValue}"]`);
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  };

  return (
    <div className="relative w-full">
      {/* Scrollable tab strip */}
      <div ref={scrollRef} className="overflow-x-auto hide-scrollbar border-b-2 border-secondary-200/50">
        <div className="flex w-max">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              data-tab={tab.value}
              type="button"
              onClick={() => handleScrollTo(tab.value)}
              className={clsx(
                'px-3 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all duration-200 flex items-center gap-1.5 shrink-0',
                activeTab === tab.value
                  ? 'border-primary-600 text-primary-700 bg-primary-50/50 rounded-t-lg'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50/50'
              )}
            >
              {tab.color && activeTab !== tab.value && (
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tab.color }}
                />
              )}
              {tab.label}
              {tab.count !== undefined && (
                <span className={clsx(
                  'ml-1 px-2 py-0.5 rounded-full text-xs font-bold',
                  activeTab === tab.value
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-600'
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ========================================
// SEARCH INPUT - Professional Design
// ========================================

export const SearchInput = ({ value, onChange, placeholder = 'Search...', className }) => (
  <div className={clsx('relative', className)}>
    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-secondary-200 bg-white text-sm text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none hover:border-secondary-300 transition-all duration-200"
    />
  </div>
);

// ========================================
// AVATAR COMPONENT - Professional Design
// ========================================

export const Avatar = ({ name, src, size = 'md', className }) => {
  const sizes = {
    sm: 'w-7 h-7 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-11 h-11 text-base',
    xl: 'w-14 h-14 text-lg',
  };

  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return src ? (
    <img src={src} alt={name} className={clsx('rounded-full object-cover ring-2 ring-white shadow-md', sizes[size], className)} />
  ) : (
    <div className={clsx(
      'rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-semibold flex items-center justify-center shadow-md ring-2 ring-white',
      sizes[size],
      className
    )}>
      {initials}
    </div>
  );
};

// ========================================
// PAGE HEADER COMPONENT - Professional Design
// ========================================

export const PageHeader = ({ title, description, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
    <div>
      <h1 className="text-xl lg:text-2xl font-bold text-secondary-900">{title}</h1>
      {description && <p className="text-sm text-secondary-500 mt-0.5">{description}</p>}
    </div>
    {action && <div className="flex gap-2">{action}</div>}
  </div>
);

// ========================================
// IMAGE PREVIEW MODAL - in-app lightbox
// ========================================
// Helper: does this attachment look like an image?
export const isImageAttachment = (att) => {
  const mime = String(att?.mimeType || att?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const ext = String(att?.url || att?.fileName || att?.originalName || '')
    .split('?')[0]
    .split('.')
    .pop()
    .toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
};

// ========================================
// TOOLTIP COMPONENT - Hover tooltip
// ========================================

export const Tooltip = ({ children, text, position = 'top' }) => {
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
    left: 'right-full top-1/2 -translate-y-1/2 mr-1.5',
    right: 'left-full top-1/2 -translate-y-1/2 ml-1.5',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-gray-800',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-gray-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-gray-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-gray-800',
  };

  return (
    <span className="relative inline-flex max-w-full group">
      {children}
      <span
        role="tooltip"
        className={clsx(
          'absolute block px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded-md whitespace-nowrap',
          'pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150',
          'z-[99999]',
          positionClasses[position]
        )}
      >
        {text}
        <span className={clsx('absolute block w-0 h-0 border-4 z-[99999]', arrowClasses[position])} />
      </span>
    </span>
  );
};

// ========================================
// CONFIRM DIALOG COMPONENT - Themed confirmation
// ========================================

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'primary'
  icon: IconComponent,
  loading = false,
}) => {
  if (!isOpen) return null;

  const variants = {
    danger: {
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      buttonBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      ringColor: 'ring-red-500',
    },
    warning: {
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      buttonBg: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
      ringColor: 'ring-amber-500',
    },
    primary: {
      iconBg: 'bg-primary-50',
      iconColor: 'text-primary-600',
      buttonBg: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
      ringColor: 'ring-primary-500',
    },
  };

  const v = variants[variant] || variants.danger;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-secondary-200/50 sm:max-w-md w-full animate-scale-in overflow-hidden">
        {/* Icon and content */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={clsx('flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', v.iconBg)}>
              {IconComponent ? (
                <IconComponent className={clsx('w-5 h-5', v.iconColor)} />
              ) : (
                <svg className={clsx('w-5 h-5', v.iconColor)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-secondary-900">{title}</h3>
              <p className="mt-1.5 text-sm text-secondary-500 leading-relaxed">{message}</p>
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="bg-secondary-50/50 px-6 py-4 flex items-center justify-end gap-3 border-t border-secondary-100">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={clsx(
              'inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2',
              v.buttonBg,
              loading && 'opacity-60 cursor-not-allowed'
            )}
          >
            {loading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : null}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ImagePreviewModal = ({ src, alt, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 animate-scale-in"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        title="Close"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <img
        src={src}
        alt={alt || 'Attachment'}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[92vh] object-contain rounded-lg shadow-2xl"
      />
    </div>
  );
};